import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CreditProvider } from './context/CreditContext';
import LandingPage from './components/landing/LandingPage';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import EmailVerification from './components/auth/EmailVerification';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetCodeVerification from './components/auth/ResetCodeVerification';
import ResetPassword from './components/auth/ResetPassword';
import PasswordResetSuccess from './components/auth/PasswordResetSuccess';
import CheckoutPage from './components/checkout/CheckoutPage';
import AddCreditPage from './components/checkout/AddCreditPage';
import SettingsPage from './components/settings/SettingsPage';
import NotificationsPage from './components/notifications/NotificationsPage';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import LanguageRoute from './components/LanguageRoute';
import './App.css';
import DashboardLayout from './components/DashboardLayout';
import './firebase'; // Initialize Firebase
import { trackPageView } from './utils/analytics';
import TermsOfUsePage from './components/legal/TermsOfUsePage';
import KVKKPage from './components/legal/KVKKPage';
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage';
import BlogPage from './components/blog/BlogPage';
import BlogDetailPage from './components/blog/BlogDetailPage';
import ServicesPage from './components/services/ServicesPage';
import ServiceDetailPage from './components/services/ServiceDetailPage';
import NotificationService from './services/notificationService';

// Redirect root to default language
const RootRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    // Detect browser language or default to Turkish
    const browserLang = navigator.language.split('-')[0];
    const defaultLang = ['tr', 'en'].includes(browserLang) ? browserLang : 'tr';
    if (location.pathname === '/') {
      window.location.href = `/${defaultLang}/home`;
    }
  }, [location]);
  return null;
};

// Redirect /:lang to /:lang/home
const LangRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  return <Navigate to={`/${lang}/home`} replace />;
};

// Redirect dashboard to social-media with language
const DashboardRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  return <Navigate to={`/${lang}/social-media`} replace />;
};

function App() {
  // Initialize notification service on app load
  useEffect(() => {
    const initNotifications = async () => {
      const notificationService = NotificationService.getInstance();
      if (notificationService.isSupported()) {
        try {
          await notificationService.initialize();
        } catch (error) {
          console.error('Failed to initialize notification service:', error);
        }
      }
    };

    initNotifications();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AuthProvider>
        <CreditProvider>
        <Routes>
          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Language routes */}
          <Route path="/:lang" element={<LanguageRoute><LangRedirect /></LanguageRoute>} />
          
          {/* Public Routes with language */}
          <Route path="/:lang/home" element={<LanguageRoute><LandingPage /></LanguageRoute>} />
          <Route path="/:lang/login" element={<LanguageRoute><Login /></LanguageRoute>} />
          <Route path="/:lang/register" element={<LanguageRoute><Register /></LanguageRoute>} />
          <Route path="/:lang/verify-email" element={<LanguageRoute><EmailVerification /></LanguageRoute>} />
          <Route path="/:lang/forgot-password" element={<LanguageRoute><ForgotPassword /></LanguageRoute>} />
          <Route path="/:lang/reset-code-verification" element={<LanguageRoute><ResetCodeVerification /></LanguageRoute>} />
          <Route path="/:lang/reset-password" element={<LanguageRoute><ResetPassword /></LanguageRoute>} />
          <Route path="/:lang/password-reset-success" element={<LanguageRoute><PasswordResetSuccess /></LanguageRoute>} />
          <Route path="/:lang/checkout" element={
            <LanguageRoute>
              <ProtectedRoute requireSubscription={false}>
                <CheckoutPage />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          <Route path="/:lang/add-credit" element={
            <LanguageRoute>
              <ProtectedRoute>
                <AddCreditPage />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          <Route path="/:lang/kullanim-kosullari" element={<LanguageRoute><TermsOfUsePage /></LanguageRoute>} />
          <Route path="/:lang/terms-of-use" element={<LanguageRoute><TermsOfUsePage /></LanguageRoute>} />
          <Route path="/:lang/kvkk-aydinlatma-metni" element={<LanguageRoute><KVKKPage /></LanguageRoute>} />
          <Route path="/:lang/kvkk" element={<LanguageRoute><KVKKPage /></LanguageRoute>} />
          <Route path="/:lang/gizlilik-politikasi" element={<LanguageRoute><PrivacyPolicyPage /></LanguageRoute>} />
          <Route path="/:lang/privacy-policy" element={<LanguageRoute><PrivacyPolicyPage /></LanguageRoute>} />
          <Route path="/:lang/blog" element={<LanguageRoute><BlogPage /></LanguageRoute>} />
          <Route path="/:lang/blog/:slug" element={<LanguageRoute><BlogDetailPage /></LanguageRoute>} />
          <Route path="/:lang/hizmetler" element={<LanguageRoute><ServicesPage /></LanguageRoute>} />
          <Route path="/:lang/services" element={<LanguageRoute><ServicesPage /></LanguageRoute>} />
          <Route path="/:lang/hizmetler/:slug" element={<LanguageRoute><ServiceDetailPage /></LanguageRoute>} />
          <Route path="/:lang/services/:slug" element={<LanguageRoute><ServiceDetailPage /></LanguageRoute>} />
          
          {/* Protected Routes - with language prefix */}
          <Route path="/:lang/social-media/*" element={
            <LanguageRoute>
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          <Route path="/:lang/web-site/*" element={
            <LanguageRoute>
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          <Route path="/:lang/ads/*" element={
            <LanguageRoute>
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          <Route path="/:lang/dashboard/*" element={
            <LanguageRoute>
              <DashboardRedirect />
            </LanguageRoute>
          } />
          <Route path="/:lang/dashboard" element={
            <LanguageRoute>
              <DashboardRedirect />
            </LanguageRoute>
          } />
          
          <Route path="/:lang/settings" element={
            <LanguageRoute>
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          
          <Route path="/:lang/notifications" element={
            <LanguageRoute>
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            </LanguageRoute>
          } />
          
          {/* Legacy routes - redirect to default language */}
          <Route path="/social-media/*" element={<Navigate to="/tr/social-media" replace />} />
          <Route path="/web-site/*" element={<Navigate to="/tr/web-site" replace />} />
          <Route path="/ads/*" element={<Navigate to="/tr/ads" replace />} />
          <Route path="/dashboard/*" element={<Navigate to="/tr/social-media" replace />} />
          <Route path="/dashboard" element={<Navigate to="/tr/social-media" replace />} />
          <Route path="/settings" element={<Navigate to="/tr/settings" replace />} />
          <Route path="/notifications" element={<Navigate to="/tr/notifications" replace />} />
          
          {/* Redirect to default language for unknown routes */}
          <Route path="*" element={<Navigate to="/tr/home" replace />} />
        </Routes>
      </CreditProvider>
      </AuthProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;
